import React from 'react'
import { Dropdown } from 'antd-mobile'
import cn from 'classnames'
import styles from './ResourcePicker.module.scss'
import { t } from '../../../config/i18n'
import { IResourceGetBusinessResources } from '../../../interfaces/rpc/IResourceGetBusinessResources'

export interface ResourcePickerProps {
  selectedResourceId: string
  selectedBusinessId: string
  resourcesByBusinessId: Record<string, IResourceGetBusinessResources>
  businessesById: Record<string, { id: string, name?: string }>
  onSelectAll: () => void
  onSelectResource: (resource: IResourceGetBusinessResources[number], businessId: string) => void
}

export const ResourcePicker = ({
  selectedResourceId,
  selectedBusinessId,
  resourcesByBusinessId,
  businessesById,
  onSelectAll,
  onSelectResource
}: ResourcePickerProps) => {
  const dropdownRef = React.useRef<any>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const groupedResources = React.useMemo(() => Object.entries(resourcesByBusinessId || {})
    .flatMap(([businessId, list]) => (list || []).map(resource => ({ resource, businessId }))),
  [resourcesByBusinessId])

  const businessName = React.useCallback((businessId: string) =>
    businessesById[businessId]?.name || '',
  [businessesById])

  const optionLabel = React.useCallback((resource: IResourceGetBusinessResources[number] | null | undefined, businessId: string) => {
    const names = [resource?.name, resource?.surname].filter(Boolean).join(' ')
    return [businessName(businessId), names].filter(Boolean).join(' / ')
  }, [businessName])

  const selectedEntry = groupedResources.find(entry =>
    entry.resource?.id === selectedResourceId && entry.businessId === selectedBusinessId
  )
  const fallbackEntry = groupedResources.find(entry => entry.resource?.id === selectedResourceId)
  const selectedLabel = selectedResourceId === 'all'
    ? t.appointment.allResources
    : selectedEntry
      ? optionLabel(selectedEntry.resource, selectedEntry.businessId)
      : fallbackEntry
        ? optionLabel(fallbackEntry.resource, fallbackEntry.businessId)
        : t.inputs.dropdownPlaceholder

  const top = !containerRef.current ? 0 : containerRef.current.getBoundingClientRect().top
  const isAllSelected = selectedResourceId === 'all'

  return (
    <Dropdown ref={dropdownRef}>
      <Dropdown.Item
        key='0'
        title={selectedLabel}
      >
        <div className="flex col"
          ref={containerRef}
          style={{ maxHeight: `calc(100vh - ${top}px)`, overflow: 'auto' }}
        >
          <div
            key='all'
            onClick={() => {
              dropdownRef.current?.close()
              onSelectAll()
            }}
            className={cn(
              'pl3 pr3 pt2 pb2 text-md',
              styles.clickableOption,
              isAllSelected && 'font-bold'
            )}
          >
            {t.appointment.allResources}
          </div>
          {!groupedResources.length
            ? <div className="pa3"> {t.inputs.dropdownNoElements} </div>
            : groupedResources.map((entry, index) => (
              <div
                key={`${entry.resource?.id}:${entry.businessId}:${index}`}
                onClick={() => {
                  dropdownRef.current?.close()
                  if (entry.resource) {
                    onSelectResource(entry.resource, entry.businessId)
                  }
                }}
                className={cn(
                  'pl3 pr3 pt2 pb2 text-md',
                  styles.clickableOption,
                  selectedResourceId === entry.resource?.id && selectedBusinessId === entry.businessId && 'font-bold'
                )}
              >
                {optionLabel(entry.resource, entry.businessId)}
              </div>
            ))}
        </div>
      </Dropdown.Item>
    </Dropdown>
  )
}
